import { Builder, withChildren } from '@builder.io/react'
import { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

const BuilderReadmore = (props) => {
  const { 
    children, 
    wrapperClasses = '',
    readMoreHeight = '800px',
    readMoreHeightMobile = '',
    buttonPosition = 'center',
    buttonSharpEdge = false,
    readMoreLabel = 'Read more',
    readLessLabel = 'Read less'
  } = props

  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const contentRef = useRef(null)

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, []);

  const handleToggleReadMore = () => {
    setIsExpanded(!isExpanded)
  }

  const getMaxHeight = () => {
    if (isExpanded && contentRef.current) {
      const scrollHeight = contentRef.current.scrollHeight
      return scrollHeight > 0 ? `${scrollHeight}px` : 'none'
    }
    // Use mobile height if provided and on mobile, otherwise use desktop height
    return (isMobile && readMoreHeightMobile) ? readMoreHeightMobile : readMoreHeight
  }

  const getButtonPositionClasses = () => {
    switch (buttonPosition) {
      case 'left':
        return 'justify-start'
      case 'right':
        return 'justify-end'
      case 'center':
      default:
        return 'justify-center'
    }
  }

  const maxHeight = getMaxHeight()

  return (
    <div className={wrapperClasses}>
      <div 
        ref={contentRef}
        className="overflow-hidden transition-all duration-200 ease-out"
        style={{ maxHeight: maxHeight }}
      >
        {children}
      </div>
      <div className={`flex ${getButtonPositionClasses()} mt-4`}>
        <button 
          onClick={handleToggleReadMore}
          className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-[#1E272E] hover:text-[#1E272E] hover:bg-gray-50 transition-all duration-200 font-medium ${buttonSharpEdge ? '' : 'rounded-md'}`}
        >
          <span>{isExpanded ? readLessLabel : readMoreLabel}</span>
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  )
}

const BuilderReadmoreWithChildren = withChildren(BuilderReadmore)

Builder.registerComponent(BuilderReadmoreWithChildren, {
  name: 'BuilderReadmore',
  canHaveChildren: true,
  inputs: [
    {
      name: 'wrapperClasses',
      type: 'string',
      friendlyName: 'Tailwind Classes for Wrapper',
    },
    {
      name: 'readMoreHeight',
      type: 'string',
      defaultValue: '800px',
      friendlyName: 'Read More Height (Desktop)',
      helperText: 'Maximum height before content is collapsed on desktop (e.g., 800px, 600px).',
    },
    {
      name: 'readMoreHeightMobile',
      type: 'string',
      defaultValue: '',
      friendlyName: 'Read More Height (Mobile)',
      helperText: 'Maximum height before content is collapsed on mobile (e.g., 400px, 300px). Leave empty to use desktop height.',
    },
    {
      name: 'buttonPosition',
      type: 'enum',
      friendlyName: 'Button Position',
      helperText: 'Choose where to position the read more button.',
      enum: ['left', 'center', 'right'],
      enumLabels: ['Left', 'Center', 'Right'],
      defaultValue: 'center',
    },
    {
      name: 'buttonSharpEdge',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'Button Sharp Edges',
      helperText: 'Toggle to remove rounded corners from the read more button.',
    },
    {
      name: 'readMoreLabel',
      type: 'string',
      defaultValue: 'Read more',
      friendlyName: 'Read More Label',
      helperText: 'Text to display on the button when content is collapsed.',
    },
    {
      name: 'readLessLabel',
      type: 'string',
      defaultValue: 'Read less',
      friendlyName: 'Read Less Label',
      helperText: 'Text to display on the button when content is expanded.',
    },
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'Builder Read More',
        },
      },
    },
  ],
})

export default BuilderReadmore